import { useEffect, useState } from "react";
import { t } from "i18next";
import { Input } from "@material-tailwind/react";
import RangeDatePicker from "../../../components/data/date/RangeDatePicker";
import TechnicianSelect from "../../../components/data/technician/TechnicianSelect";
import TechnicianVanSelect from "../../../components/data/technician-van/TechnicianVanSelect";
import SimpleSelect from "../../../components/data/simple-select/SimpleSelect";
import { createDateQuery, createDateRangeQuery, createListQuery, createSingleQuery, createTextSearchQuery, SearchQuery } from "../../../services/api/search";

interface ReplenishmentFilterFormProps {
  data?: Partial<EasyTech.Replenishment>;
  onChange: (name: string, value: any) => void;
  onSubmit: () => void;
  submitted?: boolean;
}

export const getQueriesFromFilter = (filters?: Partial<EasyTech.Replenishment>): SearchQuery[] => {
  const queries: SearchQuery[] = [];
  if (!filters) return queries;

  // Recherche par référence
  if (filters.ref) {
    queries.push(createTextSearchQuery(["ref"], filters.ref));
  }

  // Filtre par status
  if (filters.status) {
    queries.push(createSingleQuery("status", filters.status));
  }

  // Filtre par priorité
  if (filters.priority) {
    queries.push(createSingleQuery("priority", filters.priority));
  }

  // Filtre par technicien créé pour (created for)
  if (filters.createdFor) {
    if (Array.isArray(filters.createdFor)) {
      const techIds = filters.createdFor.map((t: any) => typeof t === "string" ? t : t.id);
      if (techIds.length === 1) {
        queries.push(createSingleQuery("technician.id", techIds[0]));
      } else if (techIds.length > 1) {
        queries.push(createListQuery("technician.id", techIds));
      }
    } else if (typeof filters.createdFor === "object" && (filters.createdFor as any).id) {
      queries.push(createSingleQuery("technician.id", (filters.createdFor as any).id));
    } else if (typeof filters.createdFor === "string") {
      queries.push(createSingleQuery("technician.id", filters.createdFor));
    }
  }

  // Filtre par technicien envoyé à (technician to)
  if (filters.technician) {
    if (Array.isArray(filters.technician)) {
      const techIds = filters.technician.map((t: any) => typeof t === "string" ? t : t.id);
      if (techIds.length === 1) {
        queries.push(createSingleQuery("assignedTechnician.id", techIds[0]));
      } else if (techIds.length > 1) {
        queries.push(createListQuery("assignedTechnician.id", techIds));
      }
    } else if (typeof filters.technician === "object" && (filters.technician as any).id) {
      queries.push(createSingleQuery("assignedTechnician.id", (filters.technician as any).id));
    } else if (typeof filters.technician === "string") {
      queries.push(createSingleQuery("assignedTechnician.id", filters.technician));
    }
  }

  // Filtre par van
  if (filters.van) {
    if (typeof filters.van === "object" && (filters.van as any).id) {
      queries.push(createSingleQuery("van.id", (filters.van as any).id));
    } else if (typeof filters.van === "string") {
      queries.push(createSingleQuery("van.id", filters.van));
    }
  }

  // Filtre par date
  if (filters.startDate || filters.endDate) {
    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);

      queries.push(createDateRangeQuery("created", startDate, endDate, "BETWEEN"));
    } else if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      startDate.setHours(0, 0, 0, 0);

      queries.push(createDateQuery("created", startDate, "GREATER_THAN"));
    } else if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);

      queries.push(createDateQuery("created", endDate, "LESS_THAN"));
    }
  }

  return queries;
};

const ReplenishmentFilterForm = (props: ReplenishmentFilterFormProps) => {
  const [formData, setFormData] = useState<Partial<EasyTech.Replenishment>>(
    props.data || {}
  );

  useEffect(() => {
    setFormData(props.data || {});
  }, [props.data]);

  const handleChange = (value: any, name: string) => {
    const tmp = structuredClone(formData);
    // Pour technician et createdFor, toujours un tableau
    if (name === "technician" || name === "createdFor") {
      tmp[name as keyof Partial<EasyTech.Replenishment>] = Array.isArray(value) ? value : [value];
    } else {
      tmp[name as keyof Partial<EasyTech.Replenishment>] = value;
    }
    props.onChange(name, tmp[name as keyof Partial<EasyTech.Replenishment>]);
    setFormData(tmp);
  };

  const handleDateRangeChange = (update: [Date | null, Date | null]) => {
    const [start, end] = update;
    const tmp = structuredClone(formData);
    tmp.startDate = start || undefined;
    tmp.endDate = end || undefined;
    props.onChange("startDate", tmp.startDate);
    props.onChange("endDate", tmp.endDate);
    setFormData(tmp);
  };

  const submit = (e: any) => {
    e.preventDefault();
    if (formData.startDate && !formData.endDate) return;
    if (formData.endDate && !formData.startDate) return;
    props.onSubmit();
  };

  return (
    <form onSubmit={submit} className="grid gap-4 p-2" key={"edit-replenishment-filter"}>
      <div className="grid grid-cols-1 gap-4">
        {/* Champ de référence */}
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("common:ref:label")}</label>
          <Input
            value={formData.ref || ""}
            onChange={(e) => handleChange(e.target.value, "ref")}
            className="w-full"
            size="md"
            placeholder={t("common:ref:placeholder")}
          />
        </div>

        {/* Sélecteur de status */}
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("replenishment:filters.status")}</label>
          <SimpleSelect
            value={formData.status || null}
            placeholder={t("common:all")}
            isClearable
            options={[
              { value: "CREATED", label: t("replenishment:status.CREATED") },
              { value: "SENT", label: t("replenishment:status.SENT") },
              { value: "INVENTORIED", label: t("replenishment:status.INVENTORIED") },
              { value: "CANCELLED", label: t("replenishment:status.CANCELLED") },
            ]}
            onChange={(value) => handleChange(value || undefined, "status")}
          />
        </div>

        {/* Sélecteur de priorité */}
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("replenishment:filters.priority")}</label>
          <SimpleSelect
            value={formData.priority || null}
            placeholder={t("common:all")}
            isClearable
            options={[
              { value: "NORMAL", label: t("replenishment:priority.NORMAL") },
              { value: "URGENT", label: t("replenishment:priority.URGENT") },
            ]}
            onChange={(value) => handleChange(value || undefined, "priority")}
          />
        </div>

        {/* Sélecteur de technicien créé pour (created for) */}
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("replenishment:filters.createdFor")}</label>
          <TechnicianSelect
            onChange={(technicians) => handleChange(technicians, "createdFor")}
            value={(Array.isArray(formData.createdFor) ? formData.createdFor : (formData.createdFor ? [formData.createdFor] : undefined)) || []}
            clearable={true}
            isMulti={true}
          />
        </div>

        {/* Sélecteur de technicien envoyé à (technician to) */}
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("replenishment:filters.technicianTo")}</label>
          <TechnicianSelect
            onChange={(technicians) => handleChange(technicians, "technician")}
            value={(Array.isArray(formData.technician) ? formData.technician : (formData.technician ? [formData.technician] : undefined)) || []}
            clearable={true}
            isMulti={true}
          />
        </div>

        {/* Sélecteur de van */}
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("replenishment:filters.van")}</label>
          <TechnicianVanSelect
            onChange={(van) => handleChange(van || undefined, "van")}
            value={formData.van || undefined}
          />
        </div>

        {/* Sélecteur de plage de dates */}
        <div className="field">
          <label className="block text-sm font-medium text-gray-700 mb-1">{t("workorder:filters.creationDate.label")}</label>
          <div className="flex-1 w-full">
            <RangeDatePicker
              startDate={formData.startDate}
              endDate={formData.endDate}
              onChange={handleDateRangeChange}
            />
          </div>
        </div>
      </div>
    </form>
  );
};

export default ReplenishmentFilterForm;
